import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { DictateButton } from "./DictateButton";
import {
  ConciergeBell, X, Send, CircleDashed, ListChecks, HandHeart, BookOpen, Presentation,
  Cake, CalendarDays, FlaskConical, Inbox, MessageSquareQuote, UserPlus, Sparkle, RefreshCw,
} from "lucide-react";

export const CATEGORY_META = {
  pending_followups: { label: "Follow-up", icon: ListChecks },
  promises_made: { label: "Promise", icon: HandHeart },
  publications_to_share: { label: "Publication", icon: BookOpen },
  conference_abstracts: { label: "Congress abstract", icon: Presentation },
  doctor_birthdays: { label: "Birthday", icon: Cake },
  upcoming_meetings: { label: "Meeting", icon: CalendarDays },
  scientific_requests: { label: "Scientific request", icon: FlaskConical },
  pending_medinfo: { label: "Med info", icon: Inbox },
  previous_discussion_points: { label: "Discussion point", icon: MessageSquareQuote },
  suggested_next_meetings: { label: "Next meeting", icon: UserPlus },
  potential_opportunities: { label: "Opportunity", icon: Sparkle },
};

export const URGENCY_META = {
  now: { label: "Now", cls: "text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50" },
  today: { label: "Today", cls: "text-accent-foreground bg-accent border-accent" },
  this_week: { label: "This week", cls: "text-muted-foreground bg-muted border-border" },
};

export function useAssistantNav(closePanel) {
  const navigate = useNavigate();
  return (action) => {
    const kol = action?.kol_id;
    const routes = {
      followups: "/followups",
      briefing: kol ? `/briefing/${kol}` : "/today",
      kol: kol ? `/kols/${kol}` : "/kols",
      capture: kol ? `/capture?kol=${kol}` : "/capture",
      memory: "/memory",
    };
    closePanel?.();
    navigate(routes[action?.type] || "/followups");
  };
}

export function NudgeCard({ nudge, onAction, onDismiss, compact }) {
  const cat = CATEGORY_META[nudge.category] || CATEGORY_META.pending_followups;
  const urg = URGENCY_META[nudge.urgency] || URGENCY_META.this_week;
  const Icon = cat.icon;
  return (
    <div className="group rounded-xl border border-border bg-card p-4" data-testid={`nudge-${nudge.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 rounded-full bg-muted grid place-items-center shrink-0 mt-0.5">
          <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">{cat.label}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wide border px-1.5 py-0.5 rounded-full ${urg.cls}`}>{urg.label}</span>
          </div>
          <p className="font-medium leading-snug">{nudge.title}</p>
          {!compact && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{nudge.detail}</p>}
          <button
            data-testid={`nudge-action-${nudge.id}`}
            onClick={() => onAction(nudge.action)}
            className="mt-2 text-xs font-medium text-accent-foreground hover:underline"
          >
            {ACTION_LABELS[nudge.action?.type] || "Open"} →
          </button>
        </div>
        {onDismiss && (
          <button
            data-testid={`nudge-dismiss-${nudge.id}`}
            onClick={() => onDismiss(nudge.id)}
            aria-label="Dismiss nudge"
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  followups: "Open follow-ups",
  briefing: "Open briefing",
  kol: "Open profile",
  capture: "Capture note",
  memory: "Search memory",
};

const sessionId = () => {
  let id = localStorage.getItem("assistant-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("assistant-session", id);
  }
  return id;
};

export default function AssistantPanel() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("briefing");
  const [doc, setDoc] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const startedRef = useRef(false);
  const scrollRef = useRef(null);
  const closePanel = useCallback(() => setOpen(false), []);
  const goTo = useAssistantNav(closePanel);

  const compile = useCallback(async () => {
    setCompiling(true);
    try {
      setDoc(await api.genNudges());
    } catch {
      toast.error("Could not compile your briefing");
    } finally {
      setCompiling(false);
    }
  }, []);

  const load = useCallback(() => {
    api.nudges().then((d) => {
      if (d && d.nudges) setDoc(d);
      else if (!startedRef.current) {
        startedRef.current = true;
        compile();
      }
    });
    api.chatHistory(sessionId()).then((h) => setMessages(h.messages || []));
  }, [compile]);

  useEffect(() => {
    const openHandler = () => { setOpen(true); load(); };
    window.addEventListener("open-assistant", openHandler);
    return () => window.removeEventListener("open-assistant", openHandler);
  }, [load]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, tab, open]);

  const dismiss = async (id) => {
    setDoc((d) => ({ ...d, nudges: d.nudges.filter((n) => n.id !== id) }));
    await api.dismissNudge(id).catch(() => {});
  };

  const sendText = async (raw) => {
    const text = (raw || "").trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await api.assistantChat(text, sessionId());
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I hit a snag reaching your field data — try me again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  const send = () => sendText(input);

  return (
    <>
      <motion.button
        data-testid="assistant-fab"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        aria-label="Open your chief of staff"
        className="fixed bottom-24 md:bottom-6 right-5 md:right-6 z-40 h-13 w-13 p-3.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition-opacity"
      >
        <ConciergeBell className="h-6 w-6" strokeWidth={1.5} />
      </motion.button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="assistant-panel">
          <SheetHeader className="px-5 pt-5 pb-0 text-left">
            <SheetTitle className="font-serif text-2xl flex items-center gap-2.5">
              <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center">
                <ConciergeBell className="h-5 w-5" strokeWidth={1.5} />
              </span>
              Your chief of staff
            </SheetTitle>
            <SheetDescription>Works exclusively for you. Everything below was gathered automatically.</SheetDescription>
          </SheetHeader>

          <div className="flex gap-1 px-5 mt-4 border-b border-border">
            {[["briefing", "Briefing"], ["chat", "Chat"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`assistant-tab-${key}`}
                onClick={() => setTab(key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "briefing" ? (
            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="assistant-briefing">
              {!doc ? (
                <p className="text-sm text-muted-foreground italic flex items-center gap-2 py-8 justify-center">
                  <CircleDashed className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Preparing your briefing…
                </p>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    {doc.greeting && <p className="font-serif italic text-lg leading-snug" data-testid="assistant-greeting">{doc.greeting}</p>}
                    <button data-testid="assistant-refresh-btn" onClick={compile} disabled={compiling} aria-label="Refresh briefing" className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-1">
                      <RefreshCw className={`h-4 w-4 ${compiling ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <AnimatePresence initial={false}>
                      {doc.nudges?.map((n) => (
                        <motion.div key={n.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 40 }} transition={{ duration: 0.25 }}>
                          <NudgeCard nudge={n} onAction={goTo} onDismiss={dismiss} />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {doc.nudges?.length === 0 && (
                      <p className="text-sm text-muted-foreground italic text-center py-8">All clear. Nothing needs you right now.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0" data-testid="assistant-chat">
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-sm text-muted-foreground py-6">
                    <p className="mb-3">Ask me anything about your field work:</p>
                    {["What did Dr. Rao ask me for last time?", "What should I prioritize this week?", "Any birthdays coming up?"].map((q) => (
                      <button key={q} onClick={() => setInput(q)} className="block text-left w-full text-sm border border-border rounded-lg px-3 py-2 mb-2 hover:bg-muted transition-colors">
                        “{q}”
                      </button>
                    ))}
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"
                    }`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-muted-foreground flex items-center gap-2">
                      <CircleDashed className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> thinking…
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-border p-4 flex items-center gap-2">
                <DictateButton
                  testid="assistant-dictate-btn"
                  onText={(t) => { setInput(t); setTimeout(() => sendText(t), 50); }}
                />
                <input
                  data-testid="assistant-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask your chief of staff…"
                  className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  data-testid="assistant-chat-send"
                  onClick={send}
                  disabled={sending || !input.trim()}
                  aria-label="Send message"
                  className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center hover:opacity-90 transition-opacity disabled:opacity-40 shrink-0"
                >
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

